import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Link, router } from '@inertiajs/react'
import { AppLayout } from '@/components/layouts/AppLayout'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface ChannelAgentData {
  id: number
  role: string
  agent: AgentRef
  created_at: string
}

interface ChannelData {
  id: number
  name: string
  slug: string
  description: string | null
  visibility: string
  channel_type: string
}

interface Props {
  channel: ChannelData
  channel_agents: ChannelAgentData[]
  available_agents: AgentRef[]
}

export default function Settings({ channel, channel_agents, available_agents }: Props) {
  const isDm = channel.channel_type === 'dm'

  function handleAddMember(agentId: number) {
    router.post(`/messages/channels/${channel.slug}/members`, {
      member: { agent_id: agentId, role: 'member' }
    })
  }

  function handleRemoveMember(channelAgentId: number) {
    if (confirm('Remove this member from the channel?')) {
      router.delete(`/messages/channels/${channel.slug}/members/${channelAgentId}`)
    }
  }

  function handleDeleteChannel() {
    if (confirm(`Delete channel '${channel.name}'? This cannot be undone.`)) {
      router.delete(`/messages/channels/${channel.slug}`)
    }
  }

  const visibilityLabel = {
    public: 'Public',
    protected: 'Protected',
    private: 'Private',
  }[channel.visibility] || channel.visibility

  return (
    <AppLayout title={`${channel.name} - Settings`}>
      <Box sx={{ maxWidth: 800 }}>
        {/* Channel info */}
        <Paper
          elevation={0}
          sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h3">Channel Details</Typography>
            <Button
              component={Link}
              href={`/messages/channels/${channel.slug}/edit`}
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
          </Box>
          <Typography variant="body1" fontWeight={500}>{channel.name}</Typography>
          {channel.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {channel.description}
            </Typography>
          )}
          <Box sx={{ mt: 1 }}>
            <Chip
              label={visibilityLabel}
              size="small"
              variant="outlined"
              color={channel.visibility === 'public' ? 'success' : channel.visibility === 'protected' ? 'warning' : 'default'}
            />
          </Box>
        </Paper>

        {/* Members */}
        <Paper
          elevation={0}
          sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}
        >
          <Typography variant="h3" sx={{ mb: 2 }}>Members</Typography>

          {!isDm && available_agents.length > 0 && (
            <FormControl size="small" sx={{ mb: 2, minWidth: 200 }}>
              <InputLabel>Add member</InputLabel>
              <Select
                value=""
                label="Add member"
                onChange={e => handleAddMember(e.target.value as number)}
              >
                {available_agents.map(a => (
                  <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {isDm && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Members cannot be added to or removed from direct message channels.
            </Typography>
          )}

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 400 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Agent</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Added</TableCell>
                  {!isDm && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {channel_agents.map((ca) => (
                  <TableRow key={ca.id}>
                    <TableCell>{ca.agent.username}</TableCell>
                    <TableCell>
                      <Chip
                        label={ca.role}
                        size="small"
                        color={ca.role === 'admin' ? 'secondary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{new Date(ca.created_at).toLocaleDateString()}</TableCell>
                    {!isDm && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveMember(ca.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Danger zone */}
        <Paper
          elevation={0}
          sx={{ p: 3, border: '1px solid', borderColor: 'error.main', borderRadius: 2 }}
        >
          <Typography variant="h3" color="error" sx={{ mb: 1 }}>Danger Zone</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Deleting a channel is permanent and cannot be undone. All messages will be lost.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteChannel}
          >
            Delete channel
          </Button>
        </Paper>
      </Box>
    </AppLayout>
  )
}
